import { useCallback } from 'react';

import type { FieldValues, UseFormReturn } from 'react-hook-form';

import type { OnPartialUpdate, OnUpdate } from '@/_schemas/types';

import type { UseBooleanCallbacks } from './use-boolean';

import { resetFormState } from '../utils/form-utilities';
import snackbar from '../utils/snackbar';

type EditState = [boolean, UseBooleanCallbacks];

type MutationPick<ResponseSchema, MutationSchema> = {
  mutateAsync: (
    variables: MutationSchema,
    options?: {
      onSettled?: (data: ResponseSchema | undefined) => void;
      [key: string]: unknown;
    }
  ) => Promise<ResponseSchema>;
  isPending: boolean;
  isSuccess: boolean;
};

type DeleteMutationPick<DeleteSchema> = {
  mutateAsync: (
    variables: DeleteSchema,
    options?: {
      onSettled?: (data: unknown) => void;
      [key: string]: unknown;
    }
  ) => Promise<unknown>;
  isPending: boolean;
  isSuccess: boolean;
};

type HookProps<
  FormSchema extends FieldValues,
  UpdateSchema extends object,
  ResponseSchema,
  DeleteSchema,
  MutationVariables extends object,
> = {
  form: UseFormReturn<FormSchema>;
  edit: EditState;
  formToMutation: (values: FormSchema) => UpdateSchema;
  updateMutation: MutationPick<ResponseSchema, MutationVariables>;
  deleteMutation?: DeleteMutationPick<DeleteSchema>;
  deleteVariables?: DeleteSchema;
  updateSuccessMessage: string;
  deleteSuccessMessage?: string;
  successToastId?: string;
  onSuccessSideEffects?: () => void;
  onAfterUpdateSuccess?: (response: ResponseSchema) => void;
  onDeleteSuccess?: () => void;
};

export function useProfileCrudActions<
  FormSchema extends FieldValues,
  UpdateSchema extends object,
  ResponseSchema,
  DeleteSchema = { id: string },
  MutationVariables extends object = UpdateSchema,
>({
  form,
  edit,
  formToMutation,
  updateMutation,
  deleteMutation,
  deleteVariables,
  updateSuccessMessage,
  deleteSuccessMessage = 'Record has been deleted.',
  successToastId = 'UPDATE',
  onSuccessSideEffects,
  onAfterUpdateSuccess,
  onDeleteSuccess,
}: HookProps<FormSchema, UpdateSchema, ResponseSchema, DeleteSchema, MutationVariables>) {
  const onSuccess = useCallback(
    (message: string) => {
      onSuccessSideEffects?.();
      if (successToastId) {
        snackbar.success(message, { toastId: successToastId });
      } else {
        snackbar.success(message);
      }
    },
    [onSuccessSideEffects, successToastId]
  );

  const onUpdate: OnUpdate<UpdateSchema, ResponseSchema> = useCallback(
    async (values, msg = updateSuccessMessage, cancelEdit = true, shouldResetForm = true) => {
      const response = await updateMutation.mutateAsync(values as unknown as MutationVariables, {
        onSettled: (data) => {
          if (data) {
            onAfterUpdateSuccess?.(data);
            if (shouldResetForm) {
              resetFormState(form);
            }

            if (cancelEdit) {
              edit[1].setFalse();
            }

            onSuccess(msg);
          }
        },
      });

      return response;
    },
    [edit, form, onAfterUpdateSuccess, onSuccess, updateMutation, updateSuccessMessage]
  );

  const onPartialUpdate: OnPartialUpdate<UpdateSchema, ResponseSchema> = useCallback(
    async (partialValues, msg, cancelEdit, shouldResetForm) => {
      const nValues = formToMutation(form.getValues());
      const response = await onUpdate(
        { ...nValues, ...partialValues } as UpdateSchema,
        msg,
        cancelEdit,
        shouldResetForm
      );
      return response;
    },
    [form, formToMutation, onUpdate]
  );

  const onDelete = useCallback(async () => {
    if (!deleteMutation || !deleteVariables) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deleteVariables);
      onSuccess(deleteSuccessMessage);
      onDeleteSuccess?.();
    } catch {
      // Error snackbar is handled by the API client.
    }
  }, [deleteMutation, deleteSuccessMessage, deleteVariables, onDeleteSuccess, onSuccess]);

  const isDeleting = deleteMutation?.isPending ?? false;
  const isPending = updateMutation.isPending || isDeleting;
  const isSuccess = updateMutation.isSuccess || (deleteMutation?.isSuccess ?? false);

  return {
    onSuccess,
    onUpdate,
    onPartialUpdate,
    onDelete,
    isDeleting,
    isPending,
    isSuccess,
  };
}
